import { initializeApp, getApps } from "firebase/app";
import {
	getMessaging,
	getToken,
	onMessage,
	isSupported,
} from "firebase/messaging";
import firebaseConfig, {
	firebaseVapidKey,
	isFirebaseConfigured,
} from "../firebase/config";
import useAxios from "./useAxios";

const FCM_TOKEN_STORAGE_KEY = "fcm_token";

let messagingInstance = null;

function getFirebaseApp() {
	if (getApps().length) {
		return getApps()[0];
	}
	return initializeApp(firebaseConfig);
}

function getMessagingInstance() {
	if (!messagingInstance) {
		messagingInstance = getMessaging(getFirebaseApp());
	}
	return messagingInstance;
}

async function getFcmServiceWorkerRegistration() {
	if (!("serviceWorker" in navigator)) {
		throw new Error("Service workers are not supported.");
	}

	const existingRegistration =
		await navigator.serviceWorker.getRegistration();
	if (existingRegistration?.active) {
		return existingRegistration;
	}

	return navigator.serviceWorker.ready;
}

export async function registerFcmToken() {
	if (!isFirebaseConfigured()) {
		return null;
	}
	if (!(await isSupported())) {
		return null;
	}

	const permission = await Notification.requestPermission();
	if (permission !== "granted") {
		return null;
	}

	const registration = await getFcmServiceWorkerRegistration();
	const messaging = getMessagingInstance();
	const token = await getToken(messaging, {
		vapidKey: firebaseVapidKey,
		serviceWorkerRegistration: registration,
	});

	if (!token) {
		return null;
	}

	await useAxios.post("users/fcm-token/", {
		token,
		platform: "web",
	});
	localStorage.setItem(FCM_TOKEN_STORAGE_KEY, token);
	return token;
}

export async function unregisterFcmToken() {
	const token = localStorage.getItem(FCM_TOKEN_STORAGE_KEY);
	if (!token) {
		return;
	}

	try {
		await useAxios.delete("users/fcm-token/", {
			data: { token },
		});
	} catch (error) {
		console.error("FCM token unregister failed:", error);
	} finally {
		localStorage.removeItem(FCM_TOKEN_STORAGE_KEY);
	}
}

export function setupForegroundMessageListener(onPayload) {
	if (!isFirebaseConfigured()) {
		return () => {};
	}

	const messaging = getMessagingInstance();
	return onMessage(messaging, onPayload);
}

export async function tryRegisterFcmTokenSilently() {
	try {
		if (!localStorage.getItem("access")) {
			return null;
		}
		if (Notification.permission !== "granted") {
			return null;
		}
		return await registerFcmToken();
	} catch (error) {
		console.error("FCM registration failed:", error);
		return null;
	}
}
